import { ColumnConfig, Order } from "@app/components/table/table.component";
import Table from "@app/components/table";
import TitleDescription from "@app/components/title-description";
import { Images } from "@assets/images";
import { useEffect, useState } from "react";
import { CreateVoice, Meta, Voice } from "@app/types";
import { RequestParams } from "@core/services/http/http.type";
import {
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
  SystemMessage,
} from "@app/constants";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import VoiceService from "@app/services/http/voice-train.service";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import { openPortalDialog } from "@app/services/modal.service";
import VoiceModal from "@app/components/voice-modal";
import { addToast } from "@app/components/toast/toast.service";
import ConfirmModal from "@app/components/confirm-modal";

const columnConfig: ColumnConfig[] = [
  {
    label: "ID",
    dataKey: "id",
    width: "20%",
    tdClassName: "truncate min-w-[100px] max-w-[250px]",
    hasOrder: true,
  },
  {
    label: "Tên voice",
    dataKey: "name",
    width: "20%",
    tdClassName: "truncate min-w-[100px] max-w-[250px]",
    hasOrder: true,
  },
  {
    label: "File voice",
    dataKey: "linkAudio",
    width: "55%",
    tdClassName: "truncate min-w-[180px] max-w-[280px]",
    hasOrder: true,
  },
];

const VoiceTrainManagement = () => {
  const [voices, setVoices] = useState<Voice[]>([]);
  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );

  const [meta, setMeta] = useState<Meta>();

  const [update, forceUpdate] = useForceUpdate();

  const { abortController } = useCancellationRequest();

  useEffect(() => {
    VoiceService.getList(requestParams, abortController.signal).then((res) => {
      setVoices(res.data.items);
      setMeta(res.data.meta);

      if (
        requestParams.pageNum &&
        res.data.meta.totalPage > 0 &&
        res.data.meta.totalPage < requestParams.pageNum
      ) {
        const newRequestParams: RequestParams = {
          ...requestParams,
          pageNum: DEFAULT_PAGE,
        };

        setRequestParams(newRequestParams);
      }
    });
  }, [update, requestParams]);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const handleCreateClick = () => {
    const voiceModalObs = openPortalDialog(VoiceModal);

    voiceModalObs.afterClosed().subscribe((data) => {
      if (data?.voice) {
        const createVoice: CreateVoice = {
          name: data.voice.name,
          audio: data.voice.audio,
        };

        VoiceService.create(createVoice).then(() => {
          addToast({ text: SystemMessage.ADD_SUCCESSFULLY });
          forceUpdate();
        });
      }
    });
  };

  const handleEditClick = (voice: Voice) => {
    const voiceModalObs = openPortalDialog(VoiceModal, {
      isEdit: true,
      voice,
    });

    voiceModalObs.afterClosed().subscribe((data) => {
      if (data?.voice) {
        const createVoice: CreateVoice = {
          name: data.voice.name,
          audio: data.voice.audio,
        };

        VoiceService.update(voice.id, createVoice).then(() => {
          addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
          forceUpdate();
        });
      }
    });
  };

  const handleDeleteClick = (voiceId: number) => {
    if (voiceId) {
      const confirmModalObs = openPortalDialog(ConfirmModal, {
        message: SystemMessage.DELETE_VOICE_NOTIFY,
      });

      confirmModalObs.afterClosed().subscribe((data) => {
        if (data?.isAccept) {
          VoiceService.delete(voiceId).then(() => {
            addToast({ text: SystemMessage.DELETE_SUCCESSFULLY });
            forceUpdate();
          });
        }
      });
    }
  };

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center">
        <div className="text-[#343C6A]">Danh sách voice</div>
        <div>
          <button
            className="text-xl px-6 rounded-xl leading-14 h-14 bg-color-primary hover:bg-[#667ffc] text-white"
            onClick={handleCreateClick}
          >
            <div className="flex gap-4">
              <img src={Images.PlusIcon.default} alt="" />
              <div>Tạo mới</div>
            </div>
          </button>
        </div>
      </div>
      <div className="mt-6 rounded-xl bg-white py-6 px-5">
        <TitleDescription
          title="Danh sách voice"
          onInputChange={onSearchChange}
        />
        <div className="mt-6">
          <Table
            columnConfig={columnConfig}
            dataTable={voices}
            hasCheckBox
            hasAction
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
            meta={meta}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClickOrder={onOrderChange}
            componentEmpty={
              <div className="text-center py-3 bg-[#f4f4f4]">
                <p className="text-[#666] text-xl">Không có dữ liệu</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default VoiceTrainManagement;
